import { useMutation, useQuery } from '@tanstack/react-query'

import { Campaign } from '../../models/campaign'
import { CompanyTypeSlug } from '../../models/companies'
import { baseAPICall } from '../../services/baseAPICall'
import { queryClient } from '../../services/queryClient'
import { clientInstance } from '../../utils/http-client'
import { PaginatedResponse } from 'models/paginated-response'
import { ALL_CAMPAIGNS_TABLE_KEY } from 'constants/tableQueryKeys'
import { CAMPAIGN_STATS_QUERY_KEY } from './campaign_stats_api'

/* -------------
List Campaigns
-------------- */
export async function getCampaigns(
  signal?: AbortSignal,
  queryParams?: Record<string, any>
) {
  return baseAPICall<PaginatedResponse<Campaign>>(() =>
    clientInstance.get('contacts/campaigns/', {
      signal,
      params: {
        ...queryParams,
      },
    })
  )
}

export const useGetCampaigns = (
  signal?: AbortSignal,
  disabled?: boolean,
  queryParams?: Record<string, any>
) =>
  useQuery({
    queryKey: [ALL_CAMPAIGNS_TABLE_KEY, JSON.stringify(queryParams || {})],
    queryFn: () => getCampaigns(signal, queryParams),
    refetchOnMount: true,
    staleTime: 1000 * 60 * 5,
    enabled: !disabled,
  })

/* -------------
Get Campaign Options
-------------- */
// This uses a dedicated lightweight endpoint (/campaigns/options/) that is
// significantly faster than the main campaigns list endpoint because it:
// - Doesn't calculate aggregations (contact counts, deal stats, TARO, etc.)
// - Doesn't perform lateral joins with contacts, deals, chains, etc.
// - Returns only minimal fields needed for dropdowns (id, name, color, type)
interface CampaignOption {
  label: string
  value: number | string
  icon?: React.JSX.Element
  color?: string
  companyTypeSlug?: CompanyTypeSlug
}

interface CampaignOptionResponse {
  id: number
  name: string
  color: string
  description: string
  type: {
    id: number
    name: string
    slug: CompanyTypeSlug
  }
}

export async function getCampaignsOptions(signal?: AbortSignal) {
  return baseAPICall<CampaignOptionResponse[]>(() =>
    clientInstance.get('contacts/campaigns/options/', { signal })
  )
}

/**
 * Parameters for useGetCampaignsOptions when using the options object API.
 */
interface UseGetCampaignsOptionsParams {
  /** Filter campaigns by subcategory ID or slug */
  typeOrTypeId?: CompanyTypeSlug | number
  /** AbortSignal for cancelling the request */
  signal?: AbortSignal
  /** Prepend a "No Campaign" option to the list */
  withNoneOption?: boolean
  /** Disable the query */
  disabled?: boolean
  /**
   * When true, returns empty array if typeOrTypeId is not provided.
   * Use this in deal/company edit modals to prevent selecting campaigns
   * when the subcategory is unknown, avoiding backend validation errors.
   */
  requireFilter?: boolean
}

/**
 * Helper to check if the first argument is an options object vs a filter value.
 */
function isOptionsObject(
  arg: CompanyTypeSlug | number | UseGetCampaignsOptionsParams | undefined
): arg is UseGetCampaignsOptionsParams {
  return (
    typeof arg === 'object' &&
    arg !== null &&
    !Array.isArray(arg) &&
    ('typeOrTypeId' in arg ||
      'requireFilter' in arg ||
      'withNoneOption' in arg ||
      'disabled' in arg ||
      'signal' in arg)
  )
}

/**
 * Fetches campaign options with optional filtering by subcategory.
 *
 * Supports two calling conventions for backwards compatibility:
 * 1. Options object: `useGetCampaignsOptions({ typeOrTypeId, requireFilter: true })`
 * 2. Positional args: `useGetCampaignsOptions(typeOrTypeId, signal, withNoneOption, disabled)`
 *
 * @param typeOrTypeIdOrOptions - Filter value or options object
 * @param signal - AbortSignal (positional API only)
 * @param withNoneOption - Add "No Campaign" option (positional API only)
 * @param disabled - Disable query (positional API only)
 * @returns React Query result with campaign options
 */
export const useGetCampaignsOptions = (
  typeOrTypeIdOrOptions?:
    | CompanyTypeSlug
    | number
    | UseGetCampaignsOptionsParams,
  signal?: AbortSignal,
  withNoneOption = false,
  disabled = false
) => {
  // Normalize to options object for consistent handling
  const options: UseGetCampaignsOptionsParams = isOptionsObject(
    typeOrTypeIdOrOptions
  )
    ? typeOrTypeIdOrOptions
    : {
        typeOrTypeId: typeOrTypeIdOrOptions,
        signal,
        withNoneOption,
        disabled,
      }

  const {
    typeOrTypeId,
    signal: optSignal,
    withNoneOption: optWithNoneOption = false,
    disabled: optDisabled = false,
    requireFilter = false,
  } = options

  return useQuery({
    queryKey: ['campaigns-options'],
    queryFn: () => getCampaignsOptions(optSignal),
    refetchOnMount: false,
    staleTime: 1000 * 60 * 5,
    enabled: !optDisabled,
    select: (data) => {
      const filteredData = filterCampaignsByType(
        data,
        typeOrTypeId,
        requireFilter
      )
      const campaignOptions = mapToCampaignOptions(filteredData)

      if (optWithNoneOption) {
        campaignOptions.unshift({ label: 'No Campaign', value: 'REMOVE' })
      }

      return campaignOptions
    },
  })
}

/**
 * Filters campaigns by subcategory type.
 *
 * @param data - Raw campaign data from API
 * @param typeOrTypeId - Subcategory ID (number) or slug (string) to filter by
 * @param requireFilter - If true and no filter provided, returns empty array
 * @returns Filtered campaign data
 */
function filterCampaignsByType(
  data: CampaignOptionResponse[],
  typeOrTypeId: CompanyTypeSlug | number | undefined,
  requireFilter: boolean
): CampaignOptionResponse[] {
  if (typeOrTypeId) {
    return data.filter((campaign) =>
      typeof typeOrTypeId === 'number'
        ? campaign.type?.id === typeOrTypeId
        : campaign.type?.slug === typeOrTypeId
    )
  }
  // No filter provided - return empty if requireFilter, otherwise all
  return requireFilter ? [] : data
}

/**
 * Maps raw campaign data to select option format.
 */
function mapToCampaignOptions(
  data: CampaignOptionResponse[]
): CampaignOption[] {
  return data.map((c) => ({
    label: c.name,
    value: c.id,
    companyTypeSlug: c.type?.slug,
    color: c.color,
    icon: (
      <div
        className="h-3 w-3 rounded-full mr-2 flex-shrink-0"
        style={{ backgroundColor: c.color }}
      />
    ),
  }))
}

/* -------------
Create Campaign
-------------- */
type CreateCampaignParams = {
  type_slug: CompanyTypeSlug
  product_id?: number
  name: string
  color: string
  description: string
}

async function createCampaign(values: CreateCampaignParams) {
  return baseAPICall<Campaign>(
    () => clientInstance.post('contacts/campaigns/create/', values),
    { successMessage: 'Campaign created successfully' }
  )
}

export const useCreateCampaign = () => {
  return useMutation({
    mutationFn: createCampaign,
    onMutate: async () => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey: [ALL_CAMPAIGNS_TABLE_KEY],
        exact: false,
      })
    },
    onSuccess: async () => {
      // Always refetch after error or success to ensure consistency
      await queryClient.invalidateQueries({
        queryKey: [ALL_CAMPAIGNS_TABLE_KEY],
      })
      queryClient.invalidateQueries({ queryKey: CAMPAIGN_STATS_QUERY_KEY })
    },
  })
}

/* -------------
Edit Campaign
-------------- */
type EditCampaignParams = Partial<
  Pick<CreateCampaignParams, 'name' | 'product_id' | 'color' | 'description'>
> & {
  launch_date?: string | null
  owner?: number | null
}

async function editCampaign(id: number, values: EditCampaignParams) {
  return baseAPICall<Campaign>(
    () => clientInstance.put(`contacts/campaigns/${id}/edit/`, values),
    { successMessage: 'Campaign updated successfully' }
  )
}

export const useEditCampaign = () => {
  return useMutation({
    mutationFn: ({
      id,
      product_id,
      ...values
    }: EditCampaignParams & {
      id: number
      product?: { id: number; name: string }
      product_id?: number
    }) => editCampaign(id, { ...values, product_id }),

    onMutate: async (newCampaign) => {
      // Get the main campaign list query
      const mainQueryKey = [ALL_CAMPAIGNS_TABLE_KEY]
      const mainQuery = queryClient.getQueryData(mainQueryKey)

      // Cancel any outgoing refetches to avoid optimistic update being overwritten
      await queryClient.cancelQueries({
        queryKey: mainQueryKey,
        exact: false,
      })

      // Update the campaign in all related queries
      const updateCampaign = (campaign: Campaign) => ({
        ...campaign,
        name: newCampaign.name ?? campaign.name,
        description: newCampaign.description ?? campaign.description,
        color: newCampaign.color ?? campaign.color,
        launch_date:
          newCampaign.launch_date !== undefined
            ? newCampaign.launch_date
            : campaign.launch_date,
        owner_id:
          newCampaign.owner !== undefined
            ? (newCampaign.owner ?? null)
            : campaign.owner_id,
        product:
          newCampaign.product !== undefined
            ? newCampaign.product
            : newCampaign.product_id === undefined
              ? campaign.product
              : newCampaign.product_id === null
                ? undefined
                : campaign.product?.id === newCampaign.product_id
                  ? campaign.product
                  : campaign.product,
      })

      // Apply updates to all matching queries
      queryClient.setQueriesData(
        { queryKey: mainQueryKey, exact: false },
        (old: PaginatedResponse<Campaign> | undefined) => {
          if (!old) return old
          return {
            ...old,
            results: old.results?.map((campaign) =>
              campaign.id === newCampaign.id
                ? updateCampaign(campaign)
                : campaign
            ),
          }
        }
      )

      // Return the main query data for rollback
      return {
        prev: mainQuery,
        queryKey: mainQueryKey,
      }
    },

    onError: (_, __, context) => {
      // Restore the previous data if there was an error
      if (context?.prev && context.queryKey) {
        queryClient.setQueryData(context.queryKey, context.prev)
      }
    },
  })
}

/* -------------
Delete Campaign
-------------- */
async function deleteCampaign(id: number) {
  return baseAPICall(
    () => clientInstance.delete(`contacts/campaigns/${id}/delete/`),
    {
      successMessage: 'Campaign deleted successfully',
    }
  )
}

export const useDeleteCampaign = () => {
  return useMutation({
    mutationFn: deleteCampaign,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ALL_CAMPAIGNS_TABLE_KEY] })
      queryClient.invalidateQueries({ queryKey: CAMPAIGN_STATS_QUERY_KEY })
    },
  })
}

/* -------------
Archive Campaign
-------------- */
async function archiveCampaign(id: number) {
  return baseAPICall(
    () => clientInstance.post(`contacts/campaigns/${id}/archive/`),
    {
      successMessage: 'Campaign archived',
    }
  )
}

export const useArchiveCampaign = () => {
  return useMutation({
    mutationFn: archiveCampaign,
    onSuccess: (_data, campaignId) => {
      queryClient.invalidateQueries({ queryKey: [ALL_CAMPAIGNS_TABLE_KEY] })
      queryClient.invalidateQueries({ queryKey: CAMPAIGN_STATS_QUERY_KEY })
      queryClient.invalidateQueries({ queryKey: ['campaign', campaignId] })
    },
  })
}

/* -------------
Unarchive Campaign
-------------- */
async function unarchiveCampaign(id: number) {
  return baseAPICall(
    () => clientInstance.post(`contacts/campaigns/${id}/unarchive/`),
    {
      successMessage: 'Campaign unarchived',
    }
  )
}

export const useUnarchiveCampaign = () => {
  return useMutation({
    mutationFn: unarchiveCampaign,
    onSuccess: (_data, campaignId) => {
      queryClient.invalidateQueries({ queryKey: [ALL_CAMPAIGNS_TABLE_KEY] })
      queryClient.invalidateQueries({ queryKey: CAMPAIGN_STATS_QUERY_KEY })
      queryClient.invalidateQueries({ queryKey: ['campaign', campaignId] })
    },
  })
}

/* -------------
Campaign Companies
(for contact request modal)
-------------- */
async function getCampaignCompanies(campaignId: number, signal?: AbortSignal) {
  return baseAPICall<CampaignCompaniesResponse>(() =>
    clientInstance.get(`contacts/campaigns/${campaignId}/companies/`, {
      signal,
    })
  )
}

export const useGetCampaignCompanies = (
  campaignId: number,
  signal?: AbortSignal
) =>
  useQuery({
    queryKey: ['campaign', campaignId, 'companies'],
    queryFn: () => getCampaignCompanies(campaignId, signal),
  })

export interface CampaignCompaniesResponse {
  results: Array<{
    id: number
    name: string
  }>
  total_count: number
}

export interface CampaignLeadReviewItem {
  contact_id: number
  full_name: string
  email: string | null
  title: string
  company_name: string
  qa_status: 'approved' | 'flagged' | 'rejected'
  qa_reason: string | null
  is_included: boolean
}

export interface CampaignLeadReviewResponse {
  results: CampaignLeadReviewItem[]
  summary: {
    approved: number
    flagged: number
    rejected: number
    included: number
    excluded: number
    total: number
  }
}

async function getCampaignLeadReview(campaignId: number) {
  return baseAPICall<CampaignLeadReviewResponse>(() =>
    clientInstance.get(`contacts/campaigns/${campaignId}/lead-review/`)
  )
}

async function updateCampaignLeadReview(
  campaignId: number,
  values: { contact_ids: number[]; is_included: boolean }
) {
  return baseAPICall<{ success: boolean }>(() =>
    clientInstance.patch(
      `contacts/campaigns/${campaignId}/lead-review/`,
      values
    )
  )
}

export const useGetCampaignLeadReview = (
  campaignId: number,
  disabled?: boolean
) =>
  useQuery({
    queryKey: ['campaign-lead-review', campaignId],
    queryFn: () => getCampaignLeadReview(campaignId),
    enabled: campaignId > 0 && !disabled,
  })

export const useUpdateCampaignLeadReview = (campaignId: number) =>
  useMutation({
    mutationFn: (values: { contact_ids: number[]; is_included: boolean }) =>
      updateCampaignLeadReview(campaignId, values),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['campaign-lead-review', campaignId],
      })
    },
  })

/* -------------
Campaign Refresh Status
-------------- */
export interface CampaignRefreshStatusResponse {
  is_refreshing: boolean
}

export async function getCampaignRefreshStatus(signal?: AbortSignal) {
  return baseAPICall<CampaignRefreshStatusResponse>(() =>
    clientInstance.get('contacts/campaigns/refresh-status/', { signal })
  )
}

export const useGetCampaignRefreshStatus = (options?: {
  enabled?: boolean
  refetchInterval?: number
}) =>
  useQuery({
    queryKey: ['campaigns-refresh-status'],
    queryFn: ({ signal }) => getCampaignRefreshStatus(signal),
    refetchInterval: options?.refetchInterval ?? 5000,
    enabled: options?.enabled ?? true,
    staleTime: 0,
    refetchOnMount: 'always',
  })

/* -------------
Create Campaign V2
-------------- */
type CreateCampaignV2Params = {
  name: string
  type_slug?: CompanyTypeSlug
  product_id?: number | null
  color?: string
  description?: string
  launch_date?: string | null
  owner?: number | null
}

async function createCampaignV2(values: CreateCampaignV2Params) {
  return baseAPICall<Campaign>(
    () => clientInstance.post('contacts/campaigns/create/', values),
    { successMessage: 'Campaign created successfully' }
  )
}

export const useCreateCampaignV2 = () => {
  return useMutation({
    mutationFn: createCampaignV2,
    onMutate: async () => {
      await queryClient.cancelQueries({
        queryKey: [ALL_CAMPAIGNS_TABLE_KEY],
        exact: false,
      })
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [ALL_CAMPAIGNS_TABLE_KEY],
      })
      queryClient.invalidateQueries({ queryKey: CAMPAIGN_STATS_QUERY_KEY })
    },
  })
}
